import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Divider,
  Chip,
} from "@mui/material";
import { styled, useTheme } from "@mui/system";
import { keyframes } from "@emotion/react";

// Icons for the App
import TodayIcon from '@mui/icons-material/Today'; // Date/Calendar
import AccessTimeIcon from '@mui/icons-material/AccessTime'; // Time
import LocationOnIcon from '@mui/icons-material/LocationOn'; // Location/Sauna Unit
import CheckCircleIcon from '@mui/icons-material/CheckCircle'; // Confirmation/Available

// --- Thematic Constants (Elegant & Minimalist Palette) ---
const darkGraphite = '#212121'; // Deep Charcoal/Black
const softBeige = '#f5f5f5'; // Light and clean
const goldAccent = '#b89f50'; // Luxurious Gold/Brass
const offWhite = '#ffffff'; // Card Background

// Keyframe for a slight pulse on the available slot
const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(184, 159, 80, 0.4); }
  70% { box-shadow: 0 0 0 10px rgba(184, 159, 80, 0); }
  100% { box-shadow: 0 0 0 0 rgba(184, 159, 80, 0); }
`;

// --- Styled Components ---

const AppScreenContainer = styled(Box)(({ theme }) => ({
  background: darkGraphite, // Dark frame for contrast
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 4, // Highly rounded frame
  boxShadow: theme.shadows[20],
  maxWidth: 400, // Typical phone width
  margin: '80px auto', // Center it on the page
}));

const AppContent = styled(Box)(({ theme }) => ({
  background: offWhite, // Light background for the actual app content
  height: 700, // Fixed height for a phone screen feel
  borderRadius: theme.shape.borderRadius * 3,
  padding: theme.spacing(3),
  overflowY: 'scroll',
  WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS-like devices
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  color: goldAccent,
  fontSize: '2rem',
  marginRight: theme.spacing(1),
}));

const StyledChip = styled(Chip)(({ available, selected, theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 'bold',
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius * 1,
  cursor: available ? 'pointer' : 'default',
  transition: 'all 0.2s ease-in-out',
  
  // Available but not selected
  ...(available && !selected && {
    backgroundColor: softBeige,
    color: darkGraphite,
    border: `1px solid ${darkGraphite}`,
    '&:hover': {
        backgroundColor: darkGraphite,
        color: goldAccent,
    },
  }),

  // Selected (highlighted focus)
  ...(selected && {
    backgroundColor: goldAccent,
    color: darkGraphite,
    border: `2px solid ${darkGraphite}`,
    animation: `${pulse} 1.5s infinite`,
  }),

  // Unavailable (faded out)
  ...(!available && {
    backgroundColor: theme.palette.grey[200],
    color: theme.palette.grey[500],
    textDecoration: 'line-through',
  }),
}));

const FooterButton = styled(Button)(({ theme }) => ({
    width: '100%',
    background: darkGraphite,
    color: goldAccent,
    padding: theme.spacing(2),
    fontSize: "1rem",
    marginTop: theme.spacing(3),
    '&:hover': {
        background: goldAccent,
        color: darkGraphite,
    },
}));


// --- Main Component ---

const SaunaBookingAppMockup = () => {
  const theme = useTheme();

  const mockDates = [
    { day: 'Lun 27', date: '27/10', active: false },
    { day: 'Mar 28', date: '28/10', active: true }, // Today/Focus
    { day: 'Mie 29', date: '29/10', active: false },
    { day: 'Jue 30', date: '30/10', active: false },
    { day: 'Vie 31', date: '31/10', active: false },
  ];

  const mockSlots = [
    { time: '10:00', available: true, unit: 'Alpha' },
    { time: '11:30', available: false, unit: 'Beta' },
    { time: '13:00', available: true, unit: 'Gamma' },
    { time: '14:30', available: true, unit: 'Alpha' },
    { time: '16:00', available: false, unit: 'Beta' },
    { time: '17:30', available: true, unit: 'Gamma', selected: true }, // The selected, highlighted slot
    { time: '19:00', available: true, unit: 'Alpha' },
    { time: '20:30', available: true, unit: 'Beta' },
  ];

  return (
    <Container maxWidth="lg" sx={{ textAlign: 'center', py: 8, background: softBeige }}>
      <Typography variant="h3" fontWeight="300" color={darkGraphite} mb={1}>
        La Experiencia **Haven** en tu Mano
      </Typography>
      <Typography variant="h5" color={goldAccent} mb={6} fontWeight="bold">
        Reserva, Personaliza y Accede.
      </Typography>

      <AppScreenContainer>
        <AppContent>
          
          {/* Top Bar / Status */}
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h6" fontWeight="bold">
              10:55
            </Typography>
            <Box display="flex" alignItems="center">
                <Typography variant="body2" mr={0.5}>Sauna Haven</Typography>
                <CheckCircleIcon sx={{ color: goldAccent, fontSize: '1rem' }} />
            </Box>
          </Box>

          <Typography variant="h4" fontWeight="bold" color={darkGraphite} mb={1}>
            Reservar Sesión
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            Selecciona la hora y unidad de tu preferencia.
          </Typography>

          <Divider sx={{ mb: 3, borderColor: softBeige }} />

          {/* Date Selector */}
          <Box display="flex" alignItems="center" mb={2}>
            <HeaderIcon><TodayIcon /></HeaderIcon>
            <Typography variant="h6" fontWeight="bold">Fecha</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" mb={4} sx={{ overflowX: 'scroll', WebkitOverflowScrolling: 'touch', pb: 1 }}>
            {mockDates.map((item, index) => (
              <Chip
                key={index}
                label={item.day}
                size="medium"
                sx={{
                    px: 1,
                    py: 3,
                    mr: 1,
                    minWidth: '70px',
                    fontWeight: 'bold',
                    backgroundColor: item.active ? darkGraphite : offWhite,
                    color: item.active ? goldAccent : darkGraphite,
                    border: `1px solid ${darkGraphite}`,
                }}
              />
            ))}
          </Box>
          
          {/* Time Slots */}
          <Box display="flex" alignItems="center" mb={2}>
            <HeaderIcon><AccessTimeIcon /></HeaderIcon>
            <Typography variant="h6" fontWeight="bold">Horarios Disponibles</Typography>
          </Box>
          
          <Box display="flex" flexWrap="wrap" mb={4}>
            {mockSlots.map((slot, index) => (
                <StyledChip
                    key={index}
                    label={`${slot.time} - ${slot.unit}`}
                    available={slot.available}
                    selected={slot.selected}
                />
            ))}
          </Box>
          
          <Divider sx={{ mb: 3, borderColor: softBeige }} />

          {/* Selected Summary */}
          <Box sx={{ p: 2, border: `1px solid ${goldAccent}`, borderRadius: 1 }}>
            <Typography variant="h6" fontWeight="bold" mb={1}>
                Tu Sesión Seleccionada
            </Typography>
            <Grid container spacing={1} textAlign="left">
                <Grid item xs={12} display="flex" alignItems="center">
                    <TodayIcon fontSize="small" sx={{ mr: 1, color: darkGraphite }} />
                    <Typography variant="body1">Martes, 28 de Octubre</Typography>
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center">
                    <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: darkGraphite }} />
                    <Typography variant="body1">17:30 - 19:00 (90 min)</Typography>
                </Grid>
                <Grid item xs={12} display="flex" alignItems="center">
                    <LocationOnIcon fontSize="small" sx={{ mr: 1, color: darkGraphite }} />
                    <Typography variant="body1">Unidad Gamma - VIP</Typography>
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="space-between" mt={1}>
                    <Typography variant="h6" color={goldAccent} fontWeight="bold">Costo Estimado:</Typography>
                    <Typography variant="h6" color={darkGraphite} fontWeight="bold">65€</Typography>
                </Grid>
            </Grid>
          </Box>
          
          {/* Booking Button (Footer) */}
          <FooterButton variant="contained">
            Confirmar Reserva (Pagar con App)
          </FooterButton>

          <Typography variant="caption" color="text.secondary" mt={1} display="block">
            Tu acceso digital se activará 5 minutos antes de la hora.
          </Typography>

        </AppContent>
      </AppScreenContainer>
    </Container>
  );
};

export default SaunaBookingAppMockup;